import React, { useEffect, useMemo, useState } from "react";

const TOP_CORRS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/top_correlations.json";

function normalizeTicker(s) {
  return String(s || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9.-]/g, "");
}

function fmtCorr(x) {
  if (x === null || x === undefined || Number.isNaN(Number(x))) return "";
  return Number(x).toFixed(3);
}

/**
 * Supports BOTH shapes:
 * Old:
 *   items[T] = [ {ticker, corr}, ... ]
 * New:
 *   items[T] = { positive: [...], negative: [...] }
 */
function getListForMode(node, mode) {
  if (!node) return [];

  if (Array.isArray(node)) return node;

  if (typeof node === "object") {
    const arr = node[mode];
    return Array.isArray(arr) ? arr : [];
  }

  return [];
}

export default function TopCorrs() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState("");

  // Similar vs Inverse
  const [mode, setMode] = useState("positive"); // "positive" | "negative"

  const [sortKey, setSortKey] = useState("ticker"); // "ticker" | "top1"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setErr("");

        const res = await fetch(TOP_CORRS_URL, { cache: "no-store" });
        const text = await res.text();

        if (!res.ok) {
          throw new Error(
            `HTTP ${res.status} while fetching top_correlations.json. First 120 chars: ${text.slice(
              0,
              120
            )}`
          );
        }

        if (text.trim().startsWith("<")) {
          throw new Error(
            `Got HTML instead of JSON from GitHub RAW. First 120 chars: ${text.slice(
              0,
              120
            )}`
          );
        }

        const json = JSON.parse(text);
        if (alive) setData(json);
      } catch (e) {
        if (alive) setErr(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, []);

  const items = useMemo(() => {
    return data?.items && typeof data.items === "object" ? data.items : {};
  }, [data]);

  const allTickers = useMemo(() => {
    const keys = Object.keys(items);
    const norm = keys.map(normalizeTicker).filter(Boolean);
    norm.sort();
    return norm;
  }, [items]);

  useEffect(() => {
    if (!data) return;
    if (selected) return;

    const first = allTickers[0] || "";
    setSelected(first);
    setQuery(first);
  }, [data, allTickers, selected]);

  const keyIndex = useMemo(() => {
    const m = new Map();
    for (const k of Object.keys(items)) {
      m.set(normalizeTicker(k), k);
    }
    return m;
  }, [items]);

  const selectedKey = useMemo(() => {
    const nt = normalizeTicker(selected || query);
    return keyIndex.get(nt) || "";
  }, [keyIndex, selected, query]);

  const selectedList = useMemo(() => {
    const node = selectedKey ? items[selectedKey] : null;
    const list = getListForMode(node, mode);

    return list
      .map((x) => ({
        ticker: normalizeTicker(x?.ticker),
        corr: typeof x?.corr === "number" ? x.corr : Number(x?.corr),
      }))
      .filter((x) => x.ticker && Number.isFinite(x.corr));
  }, [items, selectedKey, mode]);

  const filteredTickers = useMemo(() => {
    const q = normalizeTicker(query);
    if (!q) return allTickers;
    return allTickers.filter((t) => t.includes(q));
  }, [allTickers, query]);

  const tableRows = useMemo(() => {
    const rows = filteredTickers.map((tNorm) => {
      const actualKey = keyIndex.get(tNorm);
      const node = actualKey ? items[actualKey] : null;
      const list = getListForMode(node, mode);
      const top1 = Array.isArray(list) ? list[0] : null;

      return {
        ticker: tNorm,
        top1Ticker: top1?.ticker ? normalizeTicker(top1.ticker) : "",
        top1Corr:
          typeof top1?.corr === "number"
            ? top1.corr
            : top1?.corr != null && Number.isFinite(Number(top1.corr))
            ? Number(top1.corr)
            : null,
      };
    });

    const dir = sortDir === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      if (sortKey === "ticker") {
        return a.ticker.localeCompare(b.ticker) * dir;
      }

      const av = a.top1Corr;
      const bv = b.top1Corr;

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      return (av - bv) * dir;
    });

    return rows;
  }, [filteredTickers, items, keyIndex, sortKey, sortDir, mode]);

  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "ticker" ? "asc" : "desc");
    }
  }

  function onPickTicker(t) {
    const nt = normalizeTicker(t);
    if (!nt) return;
    setSelected(nt);
    setQuery(nt);
  }

  if (loading) {
    return <div style={{ padding: 16 }}>Loading top correlations…</div>;
  }

  if (err) {
    return <div style={{ padding: 16, color: "crimson" }}>Error: {err}</div>;
  }

  if (!data) {
    return <div style={{ padding: 16 }}>No data found.</div>;
  }

  const topK = data?.top_k ?? "";
  const tickerCount = Object.keys(items).length;
  const normalizedSelected = normalizeTicker(selected);

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>
        Top Correlations
      </h1>

      <div style={{ opacity: 0.8, marginBottom: 10 }}>
        {data.as_of ? `As of ${data.as_of}` : null}
        {topK ? ` • Top ${topK}` : null}
        {tickerCount ? ` • ${tickerCount} tickers` : null}
        {" • "}
        Mode: {mode === "positive" ? "Similar" : "Inverse"}
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search ticker (e.g., MSFT)"
          style={{
            width: 280,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            outline: "none",
          }}
        />
        <button
          onClick={() => onPickTicker(query)}
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            background: "white",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          Show
        </button>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button
            onClick={() => setMode("positive")}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: mode === "positive" ? "#f3f4f6" : "white",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Similar
          </button>
          <button
            onClick={() => setMode("negative")}
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: mode === "negative" ? "#f3f4f6" : "white",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Inverse
          </button>
        </div>
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          padding: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ fontWeight: 800, marginBottom: 8, fontSize: 16 }}>
          {selected || "(pick a ticker)"}{" "}
          <span style={{ opacity: 0.6, fontWeight: 700 }}>
            • {mode === "positive" ? "Most Similar" : "Most Inverse"}
          </span>
        </div>

        {selectedList.length === 0 ? (
          <div style={{ opacity: 0.8 }}>
            No rows for this ticker yet. (If you just updated the JSON, refresh after
            the workflow finishes.)
          </div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {selectedList.map((x) => (
              <div
                key={`${selected}-${mode}-${x.ticker}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: "#f9fafb",
                  border: "1px solid #f1f5f9",
                }}
              >
                <span
                  style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontWeight: 700,
                  }}
                >
                  {x.ticker}
                </span>
                <span
                  style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    opacity: 0.85,
                  }}
                >
                  {fmtCorr(x.corr)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: 10,
            borderBottom: "1px solid #e5e7eb",
            background: "#fafafa",
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span>All tickers</span>
          <span style={{ opacity: 0.7, fontWeight: 600 }}>
            Click a row to load the top list
          </span>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "white" }}>
                <th
                  onClick={() => toggleSort("ticker")}
                  style={{
                    textAlign: "left",
                    padding: 10,
                    borderBottom: "1px solid #e5e7eb",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Ticker{" "}
                  {sortKey === "ticker" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th
                  onClick={() => toggleSort("top1")}
                  style={{
                    textAlign: "left",
                    padding: 10,
                    borderBottom: "1px solid #e5e7eb",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {mode === "positive" ? "Most correlated" : "Most inverse"}{" "}
                  {sortKey === "top1" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th
                  onClick={() => toggleSort("top1")}
                  style={{
                    textAlign: "right",
                    padding: 10,
                    borderBottom: "1px solid #e5e7eb",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Corr {sortKey === "top1" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((r) => (
                <tr
                  key={`${mode}-${r.ticker}`}
                  onClick={() => onPickTicker(r.ticker)}
                  style={{
                    cursor: "pointer",
                    background:
                      r.ticker === normalizedSelected ? "#f3f4f6" : "white",
                  }}
                >
                  <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>
                    <span
                      style={{
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontWeight: 700,
                      }}
                    >
                      {r.ticker}
                    </span>
                  </td>
                  <td style={{ padding: 10, borderBottom: "1px solid #f1f5f9" }}>
                    {r.top1Ticker ? (
                      <span
                        style={{
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        {r.top1Ticker}
                      </span>
                    ) : (
                      <span style={{ opacity: 0.6 }}>—</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: 10,
                      borderBottom: "1px solid #f1f5f9",
                      textAlign: "right",
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      opacity: 0.85,
                    }}
                  >
                    {r.top1Corr == null ? "—" : fmtCorr(r.top1Corr)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: "pointer" }}>Raw top_correlations.json</summary>
        <pre style={{ whiteSpace: "pre-wrap", fontSize: 12, marginTop: 8 }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
}